<template>
  <div class="tasks-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-list-check"></i>
        Tarefas
      </h1>
      <button class="btn-add-task" @click="openNewTaskModal">
        <i class="fa-solid fa-plus"></i>
        Nova Tarefa
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando tarefas...</span>
      </div>
    </div>

    <!-- Board de Tarefas (estilo Trello) -->
    <div v-else class="tasks-board">
      <!-- Coluna: A Fazer -->
      <div class="task-column">
        <div class="column-header todo-header">
          <i class="fa-solid fa-circle-dot"></i>
          <h3>A Fazer</h3>
          <span class="task-count">{{ todoTasksTotal }}</span>
        </div>
        <div class="column-content" @drop="onDrop($event, 'todo')" @dragover.prevent @dragenter.prevent>
          <div
            v-for="task in todoTasks"
            :key="task.id"
            class="task-card todo-card"
            :class="{ 'task-expanded': isTaskExpanded(task.id) }"
            draggable="true"
            @dragstart="onDragStart($event, task)"
          >
            <div class="task-card-header">
              <h4 class="task-title" @click.stop="toggleTaskExpand(task.id)">
                <i class="fa-solid fa-chevron-right task-expand-icon" :class="{ 'expanded': isTaskExpanded(task.id) }"></i>
                {{ task.title }}
              </h4>
              <div class="task-actions">
                <button class="btn-action btn-edit" @click.stop="editTask(task)" title="Editar">
                  <i class="fa-solid fa-pen"></i>
                </button>
                <button class="btn-action btn-delete" @click.stop="deleteTask(task.id)" title="Excluir">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
            <div v-show="isTaskExpanded(task.id)" class="task-details">
              <p v-if="task.description" class="task-description">{{ task.description }}</p>
              <div class="task-footer">
                <span class="task-date">
                  <i class="fa-solid fa-clock"></i>
                  {{ formatDate(task.createdAt) }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="todoTasks.length === 0" class="empty-column">
            <i class="fa-solid fa-inbox"></i>
            <p>Nenhuma tarefa</p>
          </div>
          <button 
            v-if="hasMoreTodoTasks" 
            class="btn-show-more"
            @click="toggleShowAllTodo"
          >
            <i :class="showAllTodo ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            {{ showAllTodo ? 'Ver menos' : `Ver mais (${todoTasksTotal - tasksPerPage})` }}
          </button>
        </div>
      </div>

      <!-- Coluna: Em Progresso -->
      <div class="task-column">
        <div class="column-header progress-header">
          <i class="fa-solid fa-spinner"></i>
          <h3>Em Progresso</h3>
          <span class="task-count">{{ progressTasksTotal }}</span>
        </div>
        <div class="column-content" @drop="onDrop($event, 'progress')" @dragover.prevent @dragenter.prevent>
          <div
            v-for="task in progressTasks"
            :key="task.id"
            class="task-card progress-card"
            :class="{ 'task-expanded': isTaskExpanded(task.id) }"
            draggable="true"
            @dragstart="onDragStart($event, task)"
          >
            <div class="task-card-header">
              <h4 class="task-title" @click.stop="toggleTaskExpand(task.id)">
                <i class="fa-solid fa-chevron-right task-expand-icon" :class="{ 'expanded': isTaskExpanded(task.id) }"></i>
                {{ task.title }}
              </h4>
              <div class="task-actions">
                <button class="btn-action btn-edit" @click.stop="editTask(task)" title="Editar">
                  <i class="fa-solid fa-pen"></i>
                </button>
                <button class="btn-action btn-delete" @click.stop="deleteTask(task.id)" title="Excluir">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
            <div v-show="isTaskExpanded(task.id)" class="task-details">
              <p v-if="task.description" class="task-description">{{ task.description }}</p>
              <div class="task-footer">
                <span class="task-date">
                  <i class="fa-solid fa-clock"></i>
                  {{ formatDate(task.createdAt) }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="progressTasks.length === 0" class="empty-column">
            <i class="fa-solid fa-inbox"></i>
            <p>Nenhuma tarefa</p>
          </div>
          <button 
            v-if="hasMoreProgressTasks" 
            class="btn-show-more"
            @click="toggleShowAllProgress"
          >
            <i :class="showAllProgress ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            {{ showAllProgress ? 'Ver menos' : `Ver mais (${progressTasksTotal - tasksPerPage})` }}
          </button>
        </div>
      </div>

      <!-- Coluna: Concluído -->
      <div class="task-column">
        <div class="column-header done-header">
          <i class="fa-solid fa-check-circle"></i>
          <h3>Concluído</h3>
          <span class="task-count">{{ doneTasksTotal }}</span>
        </div>
        <div class="column-content" @drop="onDrop($event, 'done')" @dragover.prevent @dragenter.prevent>
          <div
            v-for="task in doneTasks"
            :key="task.id"
            class="task-card done-card"
            :class="{ 'task-expanded': isTaskExpanded(task.id) }"
            draggable="true"
            @dragstart="onDragStart($event, task)"
          >
            <div class="task-card-header">
              <h4 class="task-title" @click.stop="toggleTaskExpand(task.id)">
                <i class="fa-solid fa-chevron-right task-expand-icon" :class="{ 'expanded': isTaskExpanded(task.id) }"></i>
                {{ task.title }}
              </h4>
              <div class="task-actions">
                <button class="btn-action btn-edit" @click.stop="editTask(task)" title="Editar">
                  <i class="fa-solid fa-pen"></i>
                </button>
                <button class="btn-action btn-delete" @click.stop="deleteTask(task.id)" title="Excluir">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
            <div v-show="isTaskExpanded(task.id)" class="task-details">
              <p v-if="task.description" class="task-description">{{ task.description }}</p>
              <div class="task-footer">
                <span class="task-date">
                  <i class="fa-solid fa-clock"></i>
                  {{ formatDate(task.createdAt) }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="doneTasks.length === 0" class="empty-column">
            <i class="fa-solid fa-inbox"></i>
            <p>Nenhuma tarefa</p>
          </div>
          <button 
            v-if="hasMoreDoneTasks" 
            class="btn-show-more"
            @click="toggleShowAllDone"
          >
            <i :class="showAllDone ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            {{ showAllDone ? 'Ver menos' : `Ver mais (${doneTasksTotal - tasksPerPage})` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Tarefa -->
    <div v-if="showTaskModal" class="modal-overlay" @click.self="closeTaskModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ editingTask ? 'Editar Tarefa' : 'Nova Tarefa' }}</h2>
          <button class="btn-close" @click="closeTaskModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Título *</label>
            <input
              type="text"
              v-model="taskForm.title"
              placeholder="Digite o título da tarefa"
              maxlength="200"
              class="form-input"
            />
          </div>
          <div class="form-group">
            <label>Descrição</label>
            <textarea
              v-model="taskForm.description"
              placeholder="Digite a descrição da tarefa"
              rows="4"
              maxlength="1000"
              class="form-textarea"
            ></textarea>
          </div>
          <div class="form-group">
            <label>Status *</label>
            <select v-model="taskForm.status" class="form-select">
              <option value="todo">A Fazer</option>
              <option value="progress">Em Progresso</option>
              <option value="done">Concluído</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeTaskModal">Cancelar</button>
          <button class="btn-primary" @click="saveTask" :disabled="!taskForm.title">
            <i class="fa-solid fa-save"></i>
            Salvar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import taskService from "@/services/taskService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { useConfirm } from "@/composables/useConfirm";

export default {
  name: "ViewTasks",
  data() {
    return {
      loading: true,
      tasks: [],
      showTaskModal: false,
      editingTask: null,
      taskForm: {
        title: "",
        description: "",
        status: "todo"
      },
      draggedTask: null,
      tasksPerPage: 6,
      showAllTodo: false,
      showAllProgress: false,
      showAllDone: false,
      expandedTasks: {}
    };
  },
  computed: {
    todoTasks() {
      const tasks = this.tasks
        .filter(task => task.status === 'todo')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return this.showAllTodo ? tasks : tasks.slice(0, this.tasksPerPage);
    },
    progressTasks() {
      const tasks = this.tasks
        .filter(task => task.status === 'progress')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return this.showAllProgress ? tasks : tasks.slice(0, this.tasksPerPage);
    },
    doneTasks() {
      const tasks = this.tasks
        .filter(task => task.status === 'done')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return this.showAllDone ? tasks : tasks.slice(0, this.tasksPerPage);
    },
    todoTasksTotal() {
      return this.tasks.filter(task => task.status === 'todo').length;
    },
    progressTasksTotal() {
      return this.tasks.filter(task => task.status === 'progress').length;
    },
    doneTasksTotal() {
      return this.tasks.filter(task => task.status === 'done').length;
    },
    hasMoreTodoTasks() {
      return this.todoTasksTotal > this.tasksPerPage;
    },
    hasMoreProgressTasks() {
      return this.progressTasksTotal > this.tasksPerPage;
    },
    hasMoreDoneTasks() {
      return this.doneTasksTotal > this.tasksPerPage;
    }
  },
  async mounted() {
    this.loading = true;
    await this.loadTasks();
  },
  methods: {
    async loadTasks() {
      this.loading = true;
      try {
        const response = await taskService.getAllTasks();
        this.tasks = response.data || [];
      } catch (error) {
        toast.error("Erro ao carregar tarefas: " + handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    openNewTaskModal() {
      this.editingTask = null;
      this.taskForm = {
        title: "",
        description: "",
        status: "todo"
      };
      this.showTaskModal = true;
    },

    editTask(task) {
      this.editingTask = task;
      this.taskForm = {
        title: task.title,
        description: task.description || "",
        status: task.status
      };
      this.showTaskModal = true;
    },

    closeTaskModal() {
      this.showTaskModal = false;
      this.editingTask = null;
      this.taskForm = {
        title: "",
        description: "",
        status: "todo"
      };
    },

    async saveTask() {
      if (!this.taskForm.title) {
        toast.warning("Por favor, preencha o título da tarefa");
        return;
      }

      try {
        if (this.editingTask) {
          // Atualizar tarefa existente
          await taskService.updateTask(this.editingTask.id, this.taskForm);
          
          // Atualizar no array local
          const index = this.tasks.findIndex(t => t.id === this.editingTask.id);
          if (index !== -1) {
            this.tasks[index] = {
              ...this.tasks[index],
              ...this.taskForm
            };
          }
          
          toast.success("Tarefa atualizada com sucesso!");
        } else {
          // Criar nova tarefa
          const response = await taskService.createTask(this.taskForm);
          
          // Adicionar ao array local
          if (response.data) {
            this.tasks.push(response.data);
          }
          
          toast.success("Tarefa criada com sucesso!");
        }
        
        this.closeTaskModal();
      } catch (error) {
        toast.error("Erro ao salvar tarefa: " + handleErrorMessage(error));
      }
    },

    async deleteTask(taskId) {
      const { confirm } = useConfirm();
      
      try {
        await confirm({
          title: 'Excluir Tarefa',
          message: 'Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita.',
          confirmText: 'Sim, excluir',
          cancelText: 'Cancelar'
        });

        await taskService.deleteTask(taskId);
        
        // Remover do array local
        const index = this.tasks.findIndex(t => t.id === taskId);
        if (index !== -1) {
          this.tasks.splice(index, 1);
        }
        
        toast.success("Tarefa excluída com sucesso!");
      } catch (error) {
        // Usuário cancelou ou erro na exclusão
        if (error !== false) {
          toast.error("Erro ao excluir tarefa: " + handleErrorMessage(error));
        }
      }
    },

    // Drag and Drop
    onDragStart(event, task) {
      this.draggedTask = task;
      event.dataTransfer.effectAllowed = 'move';
    },

    async onDrop(event, newStatus) {
      if (!this.draggedTask) return;

      const oldStatus = this.draggedTask.status;
      if (oldStatus === newStatus) return;

      try {
        // Atualizar status no servidor
        await taskService.updateTask(this.draggedTask.id, { status: newStatus });
        
        // Atualizar no array local (recriando o objeto para garantir reatividade)
        const index = this.tasks.findIndex(t => t.id === this.draggedTask.id);
        if (index !== -1) {
          this.tasks[index] = {
            ...this.tasks[index],
            status: newStatus
          };
        }
        
        toast.success("Status da tarefa atualizado!");
      } catch (error) {
        // Reverter se houver erro
        const index = this.tasks.findIndex(t => t.id === this.draggedTask.id);
        if (index !== -1) {
          this.tasks[index] = {
            ...this.tasks[index],
            status: oldStatus
          };
        }
        toast.error("Erro ao atualizar status: " + handleErrorMessage(error));
      }

      this.draggedTask = null;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },

    toggleShowAllTodo() {
      this.showAllTodo = !this.showAllTodo;
    },

    toggleShowAllProgress() {
      this.showAllProgress = !this.showAllProgress;
    },

    toggleShowAllDone() {
      this.showAllDone = !this.showAllDone;
    },

    toggleTaskExpand(taskId) {
      const isExpanded = !!this.expandedTasks[taskId];
      // Criar um novo objeto para garantir reatividade
      this.expandedTasks = {
        ...this.expandedTasks,
        [taskId]: !isExpanded
      };
    },

    isTaskExpanded(taskId) {
      return !!this.expandedTasks[taskId];
    }
  }
};
</script>

<style scoped>
.tasks-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-title {
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  align-items: center;
  gap: 0.75rem;
}

.page-title i {
  color: var(--accent-primary, #3b82f6);
}

.btn-add-task {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-add-task:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.btn-add-task:active {
  transform: translateY(0);
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--accent-primary, #3b82f6);
  font-size: 1.1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
}

/* Tasks Board */
.tasks-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.task-column {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid var(--card-border);
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

.column-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 600;
}

.column-header h3 {
  flex: 1;
  margin: 0;
  font-size: 1.1rem;
}

.column-header i {
  font-size: 1.25rem;
}

.task-count {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
}

.todo-header {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: white;
}

.progress-header {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.done-header {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.column-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem;
  overflow-y: auto;
}

.task-card {
  background: var(--card-background);
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid;
  cursor: move;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: left;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.todo-card {
  border-left-color: #6366f1;
}

.progress-card {
  border-left-color: #f59e0b;
}

.done-card {
  border-left-color: #10b981;
}

.task-card-header {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 0.5rem;
}

.task-title {
  flex: 1;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  word-wrap: break-word;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  user-select: none;
  transition: color 0.2s ease;
  text-align: left;
  justify-content: flex-start;
}

.task-title:hover {
  color: var(--accent-primary);
}

.task-expand-icon {
  font-size: 0.75rem;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.task-expand-icon.expanded {
  transform: rotate(90deg);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.task-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  background: var(--bg-secondary, #f1f5f9);
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.btn-action.btn-edit:hover {
  background: var(--accent-primary, #3b82f6);
  color: white;
}

.btn-action.btn-delete {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.btn-action.btn-delete:hover {
  background: #ef4444;
  color: white;
}

.task-details {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--card-border);
  animation: slideDown 0.3s ease;
  text-align: left;
}

.task-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  text-align: left;
}

.task-description:last-child {
  margin-bottom: 0;
}

.task-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.task-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.85rem;
}

.task-date i {
  font-size: 0.8rem;
}

.empty-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  opacity: 0.5;
}

.empty-column i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.empty-column p {
  margin: 0;
  font-size: 0.9rem;
}

.btn-show-more {
  width: 100%;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;
  background: var(--bg-secondary);
  border: 2px dashed var(--card-border);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-show-more:hover {
  background: var(--card-background);
  border-color: var(--accent-primary);
  color: var(--accent-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-show-more i {
  font-size: 0.9rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: var(--card-background, #ffffff);
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--card-border, #e2e8f0);
}

[data-theme="dark"] .modal-content {
  background: var(--bg-secondary, #1a1a1d);
  border-color: var(--card-border, #52525b);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--card-border);
}

.modal-header h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
}

.btn-close {
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.9rem;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--card-border, #e2e8f0);
  border-radius: 8px;
  font-size: 1rem;
  background: var(--bg-primary, #ffffff);
  color: var(--text-primary);
  transition: all 0.2s ease;
  font-family: inherit;
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.25rem;
  padding-right: 2.5rem;
  cursor: pointer;
}

[data-theme="dark"] .form-input,
[data-theme="dark"] .form-textarea {
  background: var(--bg-secondary, #1a1a1d);
  border-color: var(--card-border, #52525b);
}

[data-theme="dark"] .form-select {
  background-color: var(--bg-secondary, #1a1a1d);
  border-color: var(--card-border, #52525b);
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.25rem;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--card-border);
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--card-border);
}

/* Responsividade */
@media (max-width: 1024px) {
  .tasks-board {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .tasks-container {
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .btn-add-task {
    width: 100%;
    justify-content: center;
  }

  .task-column {
    min-height: 400px;
  }
}
</style>

